import React, { useEffect, useState } from "react";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { BrregNameSearch, BrregOrgField } from "@/components/BrregLookup";
import { entityToFields } from "@/lib/brreg";
import { toast } from "sonner";
import { Loader2, Building2, User } from "lucide-react";

const EMPTY = { type: "company", name: "", org_number: "", contact_person: "", email: "", phone: "", address: "", postal_code: "", city: "", country: "Norge", notes: "", default_due_days: "" };

/** Opprett/rediger kunde i et sidepanel. onSaved(customer) kalles ved suksess. */
export const CustomerSheet = ({ open, onOpenChange, customer, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [brregApplied, setBrregApplied] = useState("");
  const editing = !!customer?.id;

  useEffect(() => {
    if (open) {
      setForm(customer ? { ...EMPTY, ...customer, default_due_days: customer.default_due_days ?? "" } : EMPTY);
      setError("");
      setBrregApplied("");
    }
  }, [open, customer]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  /** Fyller inn firmaopplysninger fra BRREG (navn, org.nr, adresse). Kontaktinfo beholdes. */
  const applyBrreg = (entity) => {
    const b = entityToFields(entity);
    setBrregApplied(b.org_number);
    setForm((f) => ({ ...f, type: "company", name: b.name || f.name, org_number: b.org_number || f.org_number, address: b.address || f.address, postal_code: b.postal_code || f.postal_code, city: b.city || f.city, country: b.country || f.country || "Norge" }));
    if (entity?.bankrupt || entity?.deleted_at) toast.warning(entity.deleted_at ? "Virksomheten er slettet i Enhetsregisteret" : "Virksomheten er registrert som konkurs", { description: "Kontroller før du fakturerer." });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError("Navn er påkrevd"); return; }
    setSaving(true);
    setError("");
    const payload = { ...form, default_due_days: form.default_due_days === "" ? null : Number(form.default_due_days) };
    delete payload.id; delete payload.user_id; delete payload.customer_number; delete payload.invoice_count; delete payload.invoiced_total; delete payload.outstanding; delete payload.last_invoice_date; delete payload.active; delete payload.deleted_at; delete payload.created_at; delete payload.updated_at; delete payload.invoices;
    try {
      const res = editing ? await api.put(`/sales/customers/${customer.id}`, payload) : await api.post("/sales/customers", payload);
      toast.success(editing ? "Kunde oppdatert" : "Kunde opprettet");
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg bg-card overflow-y-auto" data-testid="customer-sheet">
        <SheetHeader>
          <SheetTitle>{editing ? "Rediger kunde" : "Ny kunde"}</SheetTitle>
          <SheetDescription>{editing ? `Kundenr ${customer.customer_number}` : "Kunden får automatisk et kundenummer."}</SheetDescription>
        </SheetHeader>
        <form onSubmit={submit} className="mt-6 space-y-5" noValidate>
          <ToggleGroup type="single" value={form.type} onValueChange={(v) => v && setForm((f) => ({ ...f, type: v }))} className="grid grid-cols-2 gap-1 rounded-lg border bg-secondary p-1" data-testid="customer-type-toggle">
            <ToggleGroupItem value="company" className="gap-2 rounded-md data-[state=on]:bg-card data-[state=on]:shadow-sm" data-testid="customer-type-company"><Building2 className="h-4 w-4" /> Firma</ToggleGroupItem>
            <ToggleGroupItem value="person" className="gap-2 rounded-md data-[state=on]:bg-card data-[state=on]:shadow-sm" data-testid="customer-type-person"><User className="h-4 w-4" /> Privatperson</ToggleGroupItem>
          </ToggleGroup>
          <div className="space-y-1.5">
            <Label htmlFor="c-name">{form.type === "company" ? "Firmanavn" : "Navn"} *</Label>
            {form.type === "company" ? (
              <BrregNameSearch id="c-name" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} onSelect={applyBrreg} placeholder="Søk etter firma i Enhetsregisteret, eller skriv navnet" testIdPrefix="customer" required />
            ) : (
              <Input id="c-name" value={form.name} onChange={set("name")} required data-testid="customer-name-input" />
            )}
          </div>
          {form.type === "company" ? (
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="c-org">Org.nr</Label>
                <BrregOrgField id="c-org" value={form.org_number || ""} onChange={(v) => setForm((f) => ({ ...f, org_number: v }))} onApply={applyBrreg} autoApply={!form.name.trim()} appliedOrgNumber={brregApplied} testIdPrefix="customer" />
              </div>
              <div className="space-y-1.5 sm:col-span-2"><Label htmlFor="c-contact">Kontaktperson</Label><Input id="c-contact" value={form.contact_person || ""} onChange={set("contact_person")} data-testid="customer-contact-input" /></div>
            </div>
          ) : null}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label htmlFor="c-email">E-post (for sending)</Label><Input id="c-email" type="email" value={form.email || ""} onChange={set("email")} data-testid="customer-email-input" /></div>
            <div className="space-y-1.5"><Label htmlFor="c-phone">Telefon</Label><Input id="c-phone" value={form.phone || ""} onChange={set("phone")} data-testid="customer-phone-input" /></div>
          </div>
          <div className="space-y-1.5"><Label htmlFor="c-address">Adresse</Label><Input id="c-address" value={form.address || ""} onChange={set("address")} data-testid="customer-address-input" /></div>
          <div className="grid grid-cols-[110px_1fr] gap-4">
            <div className="space-y-1.5"><Label htmlFor="c-zip">Postnr</Label><Input id="c-zip" inputMode="numeric" value={form.postal_code || ""} onChange={set("postal_code")} data-testid="customer-zip-input" /></div>
            <div className="space-y-1.5"><Label htmlFor="c-city">Sted</Label><Input id="c-city" value={form.city || ""} onChange={set("city")} data-testid="customer-city-input" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label htmlFor="c-country">Land</Label><Input id="c-country" value={form.country || ""} onChange={set("country")} data-testid="customer-country-input" /></div>
            <div className="space-y-1.5"><Label htmlFor="c-due">Betalingsfrist (dager)</Label><Input id="c-due" type="number" min="0" max="365" value={form.default_due_days} onChange={set("default_due_days")} placeholder="Standard" data-testid="customer-due-days-input" /></div>
          </div>
          <div className="space-y-1.5"><Label htmlFor="c-notes">Notater (interne)</Label><Textarea id="c-notes" rows={3} value={form.notes || ""} onChange={set("notes")} data-testid="customer-notes-input" /></div>
          {error ? <p className="text-sm text-destructive" role="alert" data-testid="customer-form-error">{error}</p> : null}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="customer-cancel-button">Avbryt</Button>
            <Button type="submit" disabled={saving} data-testid="customer-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}{editing ? "Lagre" : "Opprett kunde"}</Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
};
